
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to migrate a Cash Flow Updates integration to Servicing'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Migrate from Cash Flow Updates' subtitle='Move an existing Cash Flow Updates integration to Servicing' mdxType="Header" />
    <p>{`The Cash Flow Updates endpoints (`}<inlineCode parentName="p">{`/cra/monitoring_insights/subscribe`}</inlineCode>{`, `}<inlineCode parentName="p">{`/cra/monitoring_insights/get`}</inlineCode>{`, and `}<inlineCode parentName="p">{`/cra/monitoring_insights/unsubscribe`}</inlineCode>{`) are deprecated and will stop functioning on August 20, 2027. To continue receiving update functionality, existing customers must migrate to the Servicing suite. Both integrations can run in parallel during the migration.`}</p>
    <p>{`Servicing delivers refreshed data through the same `}<inlineCode parentName="p">{`/cra/check_report/base_report/get`}</inlineCode>{` and `}<inlineCode parentName="p">{`/cra/check_report/income_insights/get`}</inlineCode>{` endpoints you already use at origination, rather than through a separate endpoint and report schema. For a full walkthrough, see `}<a parentName="p" {...{
        "href": "/docs/check/servicing/"
      }}>{`Servicing`}</a>{`.`}</p>
    <h4 {...{
      "id": "whats-different"
    }}>{`What's different`}</h4>
    <p>{`Cash Flow Updates uses an `}<inlineCode parentName="p">{`income_categories`}</inlineCode>{` field to limit a subscription to specific income categories. In Servicing, set `}<inlineCode parentName="p">{`income_insights_filter`}</inlineCode>{` (`}<inlineCode parentName="p">{`included_categories`}</inlineCode>{`, and optionally `}<inlineCode parentName="p">{`excluded_categories`}</inlineCode>{`) in the `}<inlineCode parentName="p">{`cra_income_insights`}</inlineCode>{` product's `}<inlineCode parentName="p">{`options`}</inlineCode>{` when creating the subscription; the filter is applied to every refreshed Income Insights report.`}</p>
    <p>{`Servicing does not currently support event-driven alerts equivalent to the `}<inlineCode parentName="p">{`CASH_FLOW_UPDATES`}</inlineCode>{` webhooks (`}<inlineCode parentName="p">{`LOW_BALANCE_DETECTED`}</inlineCode>{`, `}<inlineCode parentName="p">{`LARGE_DEPOSIT_DETECTED`}</inlineCode>{`, `}<inlineCode parentName="p">{`NSF_OVERDRAFT_DETECTED`}</inlineCode>{`, `}<inlineCode parentName="p">{`NEW_LOAN_PAYMENT_DETECTED`}</inlineCode>{`).`}</p>
    <p>{`The Cash Flow Updates report exposes deltas computed by Plaid. Servicing delivers full Base Report and Income Insights documents on each generation instead.`}</p>
    <p>{`Only Base Report and Income Insights can be retrieved for a subscription-generated report. Other Consumer Report endpoints, including `}<inlineCode parentName="p">{`/cra/check_report/pdf/get`}</inlineCode>{`, are not supported for these reports.`}</p>
    <p>{`Reports generated by a subscription can be retrieved only for servicing purposes. If you need a report you can use for a credit decision, call `}<inlineCode parentName="p">{`/cra/check_report/create`}</inlineCode>{` with a decisioning permissible purpose to generate a fresh one.`}</p>
    <h4 {...{
      "id": "update-api-subscription-creation-calls"
    }}>{`Update API subscription creation calls`}</h4>
    <p>{`Replace `}<inlineCode parentName="p">{`/cra/monitoring_insights/subscribe`}</inlineCode>{` and `}<inlineCode parentName="p">{`/cra/monitoring_insights/unsubscribe`}</inlineCode>{` with `}<inlineCode parentName="p">{`/cra/servicing/subscription/create`}</inlineCode>{` and `}<inlineCode parentName="p">{`/cra/servicing/subscription/delete`}</inlineCode>{`. Subscriptions in the new model are per-user rather than per-Item.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "#Subscribe a user to Servicing",
        "#Subscribe": true,
        "a": true,
        "user": true,
        "to": true,
        "Servicing": true
      }}>{`curl -X POST https://sandbox.plaid.com/cra/servicing/subscription/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user_id": "usr_9nSp2KuZ2x4JDw",
  "scope": "CLIENT_USER",
  "cadence": "DAILY",
  "products": [
    { "product": "cra_base_report", "version": "V1" },
    { "product": "cra_income_insights", "version": "V1" }
  ]
}'
`}</code></pre>
    <p><inlineCode parentName="p">{`/cra/servicing/subscription/create`}</inlineCode>{` returns a `}<inlineCode parentName="p">{`subscription_id`}</inlineCode>{` in the format `}<inlineCode parentName="p">{`cra-sub-<env>-<uuid>`}</inlineCode>{`, which you will need to store.`}</p>
    <h4 {...{
      "id": "update-webhooks"
    }}>{`Update webhooks`}</h4>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Register a webhook destination for the `}<strong parentName="p">{`CRA Report Updated`}</strong>{` event in the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/developers/webhooks"
          }}>{`Dashboard`}</a>{` instead of setting a URL on each subscription. You can still override the destination for an individual subscription.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Replace your listeners for `}<inlineCode parentName="p">{`CASH_FLOW_INSIGHTS_UPDATED`}</inlineCode>{` and `}<inlineCode parentName="p">{`INSIGHTS_UPDATED`}</inlineCode>{` with a single listener for `}<inlineCode parentName="p">{`CRA_REPORT_UPDATED`}</inlineCode>{`. When receiving this webhook, check the value of `}<inlineCode parentName="p">{`error_code`}</inlineCode>{`: if it is absent or `}<inlineCode parentName="p">{`null`}</inlineCode>{`, at least one product generated successfully.`}</p>
      </li>
    </ol>
    <p><inlineCode parentName="p">{`CRA_REPORT_UPDATED`}</inlineCode>{` fires once per user per day rather than once per updated Item, so a single event can reflect changes across several Items.`}</p>
    <h4 {...{
      "id": "update-report-fetch-api-calls"
    }}>{`Update report fetch API calls`}</h4>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Replace `}<inlineCode parentName="p">{`/cra/monitoring_insights/get`}</inlineCode>{` with the `}<inlineCode parentName="p">{`/get`}</inlineCode>{` endpoint for each product in the webhook's `}<inlineCode parentName="p">{`successful_products`}</inlineCode>{` field: `}<inlineCode parentName="p">{`/cra/check_report/base_report/get`}</inlineCode>{` for `}<inlineCode parentName="p">{`cra_base_report`}</inlineCode>{`, and `}<inlineCode parentName="p">{`/cra/check_report/income_insights/get`}</inlineCode>{` for `}<inlineCode parentName="p">{`cra_income_insights`}</inlineCode>{`. Pass the `}<inlineCode parentName="p">{`report_id`}</inlineCode>{` from the `}<inlineCode parentName="p">{`CRA_REPORT_UPDATED`}</inlineCode>{` webhook, as described in `}<a parentName="p" {...{
            "href": "/docs/check/servicing/#fetch-the-updated-report"
          }}>{`Fetch the updated report`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Update any code that reads the Cash Flow Updates schema to read the Base Report and Income Insights schemas instead.`}</p>
      </li>
    </ol>
    <h4 {...{
      "id": "delete-cash-flow-updates-subscriptions"
    }}>{`Delete Cash Flow Updates subscriptions`}</h4>
    <ol>
      <li parentName="ol">{`Once a user is subscribed on the new endpoints, call `}<inlineCode parentName="li">{`/cra/monitoring_insights/unsubscribe`}</inlineCode>{` so the Cash Flow Updates webhooks stop arriving for that user.`}</li>
    </ol>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "whats-different",
        level: 4,
        title: "What's different",
      },
{
        id: "update-api-subscription-creation-calls",
        level: 4,
        title: "Update API subscription creation calls",
      },
{
        id: "update-webhooks",
        level: 4,
        title: "Update webhooks",
      },
{
        id: "update-report-fetch-api-calls",
        level: 4,
        title: "Update report fetch API calls",
      },
{
        id: "delete-cash-flow-updates-subscriptions",
        level: 4,
        title: "Delete Cash Flow Updates subscriptions",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
